import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck,
  Tag,
  ListFilter,
  Upload,
  Inbox,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Phone,
  Building2,
  Mail
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { screeningAPI } from '../services/api';

const TAB_KEYS = ['settings', 'categories', 'rules', 'import', 'voicemails'];
const ACTIONS = ['ai', 'voicemail', 'block'];
const ACTION_LABEL = { ai: 'AI assistant', voicemail: 'Voicemail', block: 'Block (hang up)' };

const TAB_META = [
  { key: 'settings', label: 'Settings', icon: ShieldCheck },
  { key: 'categories', label: 'Categories', icon: Tag },
  { key: 'rules', label: 'Rules', icon: ListFilter },
  { key: 'import', label: 'Import', icon: Upload },
  { key: 'voicemails', label: 'Voicemails', icon: Inbox }
];

export default function CallScreeningPage() {
  const [tab, setTab] = useState('settings');
  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-blue-600" /> Call Screening
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Route inbound calls to AI, voicemail, or block them — by contact or category.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200 mb-6">
        {TAB_META.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'settings' && <SettingsTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'rules' && <RulesTab />}
      {tab === 'import' && <ImportTab />}
      {tab === 'voicemails' && <VoicemailsTab />}
    </div>
  );
}

function SettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    defaultUnknownAction: 'ai',
    defaultRestrictedAction: 'voicemail',
    transcribeVoicemail: true,
    voicemailGreeting: '',
    notificationEmail: ''
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await screeningAPI.getSettings();
      const s = data.settings || {};
      setSettings({
        enabled: s['screening.enabled'] === 'true',
        defaultUnknownAction: s['screening.default_unknown_action'] || 'ai',
        defaultRestrictedAction: s['screening.default_restricted_action'] || 'voicemail',
        transcribeVoicemail: (s['screening.transcribe_voicemail'] ?? 'true') === 'true',
        voicemailGreeting: s['screening.voicemail_greeting'] || '',
        notificationEmail: s['screening.notification_email'] || ''
      });
    } catch (error) {
      toast.error('Failed to load screening settings');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      await screeningAPI.updateSettings(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 bg-white rounded-lg border border-gray-200 p-6">
      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          className="w-4 h-4 text-blue-600 rounded"
        />
        <span className="font-medium text-gray-900">Enable call screening</span>
        <span className="text-xs text-gray-500">When off, every call reaches the AI.</span>
      </label>

      <ActionSelect
        label="Unknown callers (no matching contact)"
        value={settings.defaultUnknownAction}
        onChange={(v) => setSettings({ ...settings, defaultUnknownAction: v })}
      />

      <ActionSelect
        label="Restricted / anonymous caller ID"
        value={settings.defaultRestrictedAction}
        onChange={(v) => setSettings({ ...settings, defaultRestrictedAction: v })}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Voicemail greeting
        </label>
        <textarea
          value={settings.voicemailGreeting}
          onChange={(e) => setSettings({ ...settings, voicemailGreeting: e.target.value })}
          rows={3}
          placeholder="Leave blank for the default greeting."
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={settings.transcribeVoicemail}
          onChange={(e) => setSettings({ ...settings, transcribeVoicemail: e.target.checked })}
          className="w-4 h-4 text-blue-600 rounded"
        />
        <span className="font-medium text-gray-900">Transcribe voicemails with Whisper</span>
      </label>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Voicemail notification email
        </label>
        <input
          type="email"
          value={settings.notificationEmail}
          onChange={(e) => setSettings({ ...settings, notificationEmail: e.target.value })}
          placeholder="Defaults to the first admin user"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save
      </button>
    </div>
  );
}

function ActionSelect({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
      >
        {ACTIONS.map((a) => (
          <option key={a} value={a}>
            {ACTION_LABEL[a]}
          </option>
        ))}
      </select>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await screeningAPI.getCategories();
      setCategories(data.categories || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    if (!name.trim()) return;
    try {
      await screeningAPI.createCategory({ name: name.trim(), description: description.trim() || null });
      toast.success('Category created');
      setName('');
      setDescription('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create category');
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this category? Rules referencing it will be deleted too.')) return;
    try {
      await screeningAPI.deleteCategory(id);
      toast.success('Category deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-4 flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Banking, Military, Family"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={create}
          className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : categories.length === 0 ? (
        <EmptyState text="No categories yet. Add one above to start grouping contacts." />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-gray-900">{c.name}</div>
                {c.description && (
                  <div className="text-sm text-gray-500">{c.description}</div>
                )}
              </div>
              <button
                onClick={() => remove(c.id)}
                className="text-red-600 hover:text-red-800 p-2"
                title="Delete category"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RulesTab() {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ scope: 'category', contactCategoryId: '', contactId: '', action: 'voicemail' });
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [rulesRes, catsRes] = await Promise.all([
        screeningAPI.getRules(),
        screeningAPI.getCategories()
      ]);
      setRules(rulesRes.data.rules || []);
      setCategories(catsRes.data.categories || []);
    } catch {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const searchContacts = async (q) => {
    setContactSearch(q);
    if (!q || q.length < 2) {
      setContactResults([]);
      return;
    }
    try {
      // Reuse the existing /api/contacts endpoint, which supports search
      const res = await fetch(
        `${(import.meta.env.VITE_API_BASE_URL || '/api')}/contacts?search=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      const data = await res.json();
      setContactResults((data.contacts || []).slice(0, 10));
    } catch {
      setContactResults([]);
    }
  };

  const create = async () => {
    try {
      const payload = { action: form.action };
      if (form.scope === 'category') {
        if (!form.contactCategoryId) return toast.error('Pick a category');
        payload.contactCategoryId = form.contactCategoryId;
      } else {
        if (!form.contactId) return toast.error('Pick a contact');
        payload.contactId = form.contactId;
      }
      await screeningAPI.createRule(payload);
      toast.success('Rule created');
      setForm({ scope: 'category', contactCategoryId: '', contactId: '', action: 'voicemail' });
      setContactSearch('');
      setContactResults([]);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create rule');
    }
  };

  const toggle = async (rule) => {
    try {
      await screeningAPI.updateRule(rule.id, { enabled: !rule.enabled });
      load();
    } catch {
      toast.error('Failed to toggle rule');
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await screeningAPI.deleteRule(id);
      load();
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex gap-3">
          <button
            onClick={() => setForm({ ...form, scope: 'category', contactId: '' })}
            className={clsx(
              'px-3 py-1.5 rounded-md text-sm',
              form.scope === 'category' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            )}
          >
            Rule for a category
          </button>
          <button
            onClick={() => setForm({ ...form, scope: 'contact', contactCategoryId: '' })}
            className={clsx(
              'px-3 py-1.5 rounded-md text-sm',
              form.scope === 'contact' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            )}
          >
            Rule for a specific contact
          </button>
        </div>

        {form.scope === 'category' ? (
          <select
            value={form.contactCategoryId}
            onChange={(e) => setForm({ ...form, contactCategoryId: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">— pick a category —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        ) : (
          <div className="space-y-2">
            <input
              value={contactSearch}
              onChange={(e) => searchContacts(e.target.value)}
              placeholder="Search contacts by name or phone"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            {contactResults.length > 0 && (
              <div className="border border-gray-200 rounded-md divide-y max-h-48 overflow-y-auto">
                {contactResults.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setForm({ ...form, contactId: c.id });
                      setContactSearch(`${c.name} (${c.phoneNumber})`);
                      setContactResults([]);
                    }}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-sm hover:bg-gray-50',
                      form.contactId === c.id && 'bg-blue-50'
                    )}
                  >
                    <span className="font-medium">{c.name}</span>{' '}
                    <span className="text-gray-500">{c.phoneNumber}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <ActionSelect
          label="Action"
          value={form.action}
          onChange={(v) => setForm({ ...form, action: v })}
        />

        <button
          onClick={create}
          className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> Add rule
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : rules.length === 0 ? (
        <EmptyState text="No rules yet. Add one above." />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y">
          {rules.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-gray-900">
                  {r.contact ? (
                    <>Contact: {r.contact.name} <span className="text-gray-500 font-normal">({r.contact.phoneNumber})</span></>
                  ) : (
                    <>Category: {r.contactCategory?.name}</>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Action: <span className="font-medium">{ACTION_LABEL[r.action]}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggle(r)}
                  className={clsx(
                    'px-2 py-1 text-xs rounded',
                    r.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {r.enabled ? 'Enabled' : 'Disabled'}
                </button>
                <button
                  onClick={() => remove(r.id)}
                  className="text-red-600 hover:text-red-800 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImportTab() {
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    screeningAPI.getCategories().then(({ data }) => setCategories(data.categories || []));
  }, []);

  const submit = async () => {
    if (!file) return toast.error('Pick a .vcf or .csv file first');
    try {
      setBusy(true);
      const form = new FormData();
      form.append('file', file);
      if (categoryId) form.append('categoryId', categoryId);
      const { data } = await screeningAPI.importContacts(form);
      setResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 bg-white rounded-lg border border-gray-200 p-6">
      <p className="text-sm text-gray-600">
        Upload a vCard (.vcf) export from your phone or a CSV with name/phone columns.
        Optionally assign every imported contact to a category.
      </p>
      <input
        type="file"
        accept=".vcf,.csv,text/vcard,text/csv"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="block text-sm"
      />
      <div>
        <label className="block text-sm text-gray-600 mb-1">Assign to category (optional)</label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="">— none —</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <button
        onClick={submit}
        disabled={busy || !file}
        className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        Import
      </button>
      {result && (
        <div className="bg-gray-50 rounded-md p-3 text-sm">
          <div>Parsed: {result.parsed}</div>
          <div className="text-green-700">Created: {result.created}</div>
          <div className="text-blue-700">Updated: {result.updated}</div>
          <div className="text-gray-600">Skipped: {result.skipped}</div>
          {result.errors?.length > 0 && (
            <div className="text-red-600 mt-1">{result.errors.length} errors</div>
          )}
        </div>
      )}
    </div>
  );
}

function VoicemailsTab() {
  const [voicemails, setVoicemails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await screeningAPI.getVoicemails();
      setVoicemails(data.voicemails || []);
    } catch {
      toast.error('Failed to load voicemails');
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (vm) => {
    try {
      await screeningAPI.updateVoicemail(vm.id, { isRead: !vm.isRead });
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this voicemail?')) return;
    try {
      await screeningAPI.deleteVoicemail(id);
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (voicemails.length === 0) return <EmptyState text="No voicemails yet." />;

  return (
    <div className="bg-white rounded-lg border border-gray-200 divide-y">
      {voicemails.map((vm) => (
        <div key={vm.id} className={clsx('p-4', !vm.isRead && 'bg-blue-50/40')}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm text-gray-900">
                <Phone className="w-4 h-4 text-gray-500" />
                <span className="font-medium">{vm.callerNumber}</span>
                {vm.duration != null && (
                  <span className="text-gray-500">· {vm.duration}s</span>
                )}
                <span className="text-gray-400 text-xs ml-auto">
                  {new Date(vm.createdAt).toLocaleString()}
                </span>
              </div>
              {vm.transcription ? (
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{vm.transcription}</p>
              ) : (
                <p className="text-sm text-gray-400 italic mt-2">No transcription.</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => markRead(vm)}
                className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                {vm.isRead ? 'Mark unread' : 'Mark read'}
              </button>
              <button
                onClick={() => remove(vm.id)}
                className="text-red-600 hover:text-red-800 p-1 self-end"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
      {text}
    </div>
  );
}
